import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiSearch, FiArrowRight } from 'react-icons/fi';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { CATEGORIES, TOOLS, searchTools } from '../data/registry';
import { SEO } from '../components/layout/SEO';

export const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const activeCategory = searchParams.get('category') || 'All';
  
  const [localSearch, setLocalSearch] = useState(initialSearch);
  const [filteredTools, setFilteredTools] = useState(TOOLS);

  // Sync state when searchParams change from external sources (like Header)
  useEffect(() => {
    const search = searchParams.get('search') || '';
    setLocalSearch(search);
  }, [searchParams]);

  // Live filter effect
  useEffect(() => {
    let result = searchTools(localSearch);
    if (activeCategory !== 'All') {
      result = result.filter(t => t.category === activeCategory);
    }
    setFilteredTools(result);
  }, [localSearch, activeCategory]);

  return (
    <div className="w-full relative overflow-hidden">
      <SEO 
        title="FreeToolSuitePro — 100% Free & Private Online Tools"
        description="Your comprehensive suite of 100% free, client-side browser tools. No server uploads, total privacy, incredibly fast processing."
        url="https://freetoolsuitepro.com/"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "FreeToolSuitePro",
          "url": "https://freetoolsuitepro.com/"
        }}
      />

      {/* Hero Section */}
      <section className="relative px-4 py-20 md:py-32 overflow-hidden bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-100/40 via-transparent to-transparent dark:from-primary-900/20"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-6"
          >
            The Ultimate <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-blue-400">Free Suite</span><br className="hidden md:block"/> of Online Tools
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg md:text-xl text-slate-600 dark:text-slate-400 mb-10 max-w-2xl mx-auto px-4"
          >
            100% private. 100% free. Everything runs securely in your browser without ever uploading files to a server.
          </motion.p>

          {/* Big Search Bar */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative max-w-2xl mx-auto"
          >
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-blue-400 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-200"></div>
              <div className="relative flex items-center bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-2">
                <FiSearch className="w-6 h-6 text-slate-400 ml-3 shrink-0" />
                <input 
                  type="text" 
                  value={localSearch}
                  onChange={(e) => {
                    setLocalSearch(e.target.value);
                    if (e.target.value) {
                      setSearchParams({ search: e.target.value });
                    } else {
                      setSearchParams({});
                    }
                  }}
                  placeholder="What tool do you need today?"
                  className="w-full bg-transparent border-none outline-none px-4 py-3 text-base md:text-lg text-slate-900 dark:text-white placeholder:text-slate-400"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools Grid Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 justify-center mb-12">
          <button 
            onClick={() => setSearchParams({})}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCategory === 'All' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-md' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
          >
            All Tools
          </button>
          {CATEGORIES.map(cat => (
            <button 
              key={cat}
              onClick={() => setSearchParams({ category: cat })}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCategory === cat ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-md' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Results Info */}
        {localSearch && (
          <p className="text-slate-500 dark:text-slate-400 mb-6 text-center">
            Found {filteredTools.length} tool{filteredTools.length !== 1 ? 's' : ''} matching "{localSearch}"
          </p>
        )}

        {/* Grid */}
        {filteredTools.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTools.map((tool, index) => (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                key={tool.slug}
              >
                <Link 
                  to={`/tools/${tool.slug}`} 
                  className="block h-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 hover:shadow-xl hover:border-primary-300 dark:hover:border-primary-700 transition-all duration-300 group relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-primary-100 to-transparent dark:from-primary-900/30 opacity-0 group-hover:opacity-100 transition-opacity rounded-bl-full"></div>
                  
                  <div className="w-12 h-12 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <SafeIcon name={tool.icon} className="w-6 h-6" />
                  </div>
                  
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center justify-between">
                    {tool.name}
                    <FiArrowRight className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-primary-500" />
                  </h3>
                  
                  <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2">
                    {tool.description}
                  </p>
                  
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                    <span className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                      {tool.category}
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 mb-4">
              <FiSearch size={24} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No tools found</h3>
            <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto">
              We couldn't find any tools matching your criteria. Try adjusting your search or category filter.
            </p>
            <button 
              onClick={() => { setLocalSearch(''); setSearchParams({}); }}
              className="mt-6 px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white rounded-lg font-medium transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </section>
    </div>
  );
};